import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { NA_VALUE, ProductType } from '@yme/common/constants';
import { useIsMount, useWindowSize } from '@yme/hooks/index';
import { getProductFilterOptions, setFilterOptions } from '@yme/pdp-components/ProductCard';
import { InformationAlerts } from '@yme/pdp-components/ProductCard/components/InformationAlerts';
import { getCommon, getIsMotorcycleVariationsEnabled } from '@yme/selectors/common';
import { getProductVariantsByColorWithGroupingProducts } from '@yme/selectors/products';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RequiredPartsContainer } from '../RequiredPartsContainer';
import { SelectAccessorySize } from './AccessoryAndApparelFilter';
import { SelectLength } from './BoatsFilter';
import { SelectFrameSize } from './EBikesFilter';
import { EngineFilters, useMarineEnginesFilter } from './EngineFilter';
import { useMotorcyclesFilter, MotorcyclesFilters } from './MotorcyclesFilter';
import { SelectScootersOptions } from './ScootersFilter';
import { SelectColor } from './SelectColor';
import { STATUS_INIT } from './constants';
import { getPrepareFilterOptionsHelper } from './helpers';
import { useProductOption } from './productOptions';
import { useDefaultFilter } from './useDefaultFilter';
import { useSizeFilter } from './useSizeFilter';
export function ProductFilter({ currentProduct, smartfitKey, sizeGuideAssetPath, productType = '', compatibilityAlert, lowStockAlert, isCompatibilityCheckAllowed, isRequiredProductExist, requiredProducts = [], plpUrl = '', openSelectModelModal, addRequiredProduct, removeRequiredProduct, privacyConsentsModel, motorcyclesInfoLink, scooterPackagesInfoLink, fasletModel, labels, }) {
    const productVariantsByColor = useSelector(getProductVariantsByColorWithGroupingProducts);
    const productFilterOptions = useSelector(getProductFilterOptions);
    const isMotorcycleVariationsEnabled = useSelector(getIsMotorcycleVariationsEnabled);
    const { outboardEnginesSelectionLink } = useSelector(getCommon);
    const dispatch = useDispatch();
    const isUnit = productType === ProductType.UNIT;
    const isAccessory = productType === ProductType.ACCESSORY;
    const isApparel = productType === ProductType.APPAREL;
    const isAccessoryOrApparel = isAccessory || isApparel;
    const { isInfiniteDesktopView, isMobileView } = useWindowSize();
    const { colorItemIndex = 0 } = productFilterOptions;
    const history = useHistory();
    const isMount = useIsMount();
    const colorVariants = useMemo(() => productVariantsByColor || [], [productVariantsByColor]);
    const { isMarineEnginesFilter, onChangeColor: onChangeColorMarineEnginesFilter } = useMarineEnginesFilter({
        productVariantsByColor,
        productFilterOptions,
        colorVariants,
    });
    const { isMotorcyclesFilter, onChangeColor: onChangeColorMotorcyclesFilter } = useMotorcyclesFilter({
        productVariantsByColor,
        productFilterOptions,
        colorVariants,
    });
    const { isBoatsFilter, isScootersFilter, onChangeColor: onChangeProductOptionsFilter, } = useProductOption({
        productVariantsByColor,
        productFilterOptions,
        colorVariants,
    });
    const { isSizeFilterAllow, isEBikesFilter, onChangeColor: onChangeColorSizeFilter, } = useSizeFilter({
        productVariantsByColor,
        productFilterOptions,
        colorVariants,
        isAccessoryOrApparel,
    });
    const { onChangeColor: onChangeColorDefault } = useDefaultFilter({
        productVariantsByColor,
        productFilterOptions,
        colorVariants,
    });
    const variantsOfProduct = useMemo(() => productVariantsByColor ? productVariantsByColor[colorItemIndex]?.variantsOfProduct || [] : [], [colorItemIndex, productVariantsByColor]);
    const commonFilterProps = {
        productFilterOptions,
        variantsOfProduct,
        currentProduct,
    };
    const onChangeColor = useCallback(() => {
        return (index, colourId) => {
            if (isSizeFilterAllow) {
                return onChangeColorSizeFilter(index, colourId);
            }
            if (isMarineEnginesFilter) {
                return onChangeColorMarineEnginesFilter(index, colourId);
            }
            if (isMotorcyclesFilter) {
                return onChangeColorMotorcyclesFilter(index, colourId);
            }
            if (isBoatsFilter || isScootersFilter) {
                return onChangeProductOptionsFilter(index, colourId);
            }
            return onChangeColorDefault(index, colourId);
        };
    }, [
        isSizeFilterAllow,
        isMarineEnginesFilter,
        isMotorcyclesFilter,
        isBoatsFilter,
        isScootersFilter,
        onChangeColorDefault,
        onChangeColorSizeFilter,
        onChangeColorMarineEnginesFilter,
        onChangeColorMotorcyclesFilter,
        onChangeProductOptionsFilter,
    ]);
    const isRequiredPartsShowed = isAccessory &&
        openSelectModelModal &&
        addRequiredProduct &&
        removeRequiredProduct &&
        labels &&
        isRequiredProductExist &&
        isCompatibilityCheckAllowed;
    const isFrameSizeBlockVisible = variantsOfProduct?.some((item) => {
        return Boolean(item.size !== NA_VALUE && item.size.trim() !== '');
    });
    useEffect(() => {
        const listenHistory = history.listen((_, action) => {
            const pageHash = history.location.hash.replace('#', '').trim();
            if (action === 'POP' && !pageHash) {
                const prepareFilterOptionsHelper = getPrepareFilterOptionsHelper({
                    isMarineEnginesFilter,
                    isMotorcyclesFilter,
                    isProductOptionFilter: isBoatsFilter || isScootersFilter,
                    isSizeFilter: isSizeFilterAllow,
                });
                const currentVariant = prepareFilterOptionsHelper
                    ? prepareFilterOptionsHelper({
                        colorVariants,
                        index: 0,
                        sku: pageHash ?? '',
                        statusType: STATUS_INIT,
                    })
                    : null;
                if (currentVariant?.sku) {
                    dispatch(setFilterOptions(currentVariant));
                }
            }
        });
        return () => {
            listenHistory();
        };
    }, [
        colorVariants,
        dispatch,
        history,
        isBoatsFilter,
        isMarineEnginesFilter,
        isMotorcyclesFilter,
        isScootersFilter,
        isSizeFilterAllow,
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement(InformationAlerts, { filterTypes: {
                isMarineEnginesFilter,
                isMotorcyclesFilter,
                isScootersFilter,
            }, linksAddresses: {
                motorcyclesInfoLink,
                scooterPackagesInfoLink,
                outboardEnginesSelectionLink,
            } }),
        React.createElement(SelectColor, { isAccessory: isAccessory, colorVariants: colorVariants, colorItemIndex: colorItemIndex, onChangeColor: onChangeColor() }),
        compatibilityAlert && !isInfiniteDesktopView && (React.createElement(FlexWrapper, { isFullWidth: true, mt: "24px", mb: "32px" }, compatibilityAlert)),
        isRequiredPartsShowed && (React.createElement(RequiredPartsContainer, { requiredProducts: requiredProducts, addRequiredProduct: addRequiredProduct, removeRequiredProduct: removeRequiredProduct, labels: labels, plpUrl: plpUrl, selectModelHandler: openSelectModelModal, privacyConsentsModel: privacyConsentsModel })),
        isUnit && isMarineEnginesFilter && React.createElement(EngineFilters, { ...commonFilterProps }),
        isUnit && isMotorcyclesFilter && isMotorcycleVariationsEnabled && (React.createElement(MotorcyclesFilters, { isMobileView: isMobileView, ...commonFilterProps })),
        isUnit && isScootersFilter && (React.createElement(SelectScootersOptions, { ...commonFilterProps, isMobileView: isMobileView })),
        isUnit && isBoatsFilter && React.createElement(SelectLength, { ...commonFilterProps }),
        !isMount && isUnit && isEBikesFilter && smartfitKey && isFrameSizeBlockVisible && (React.createElement(SelectFrameSize, { ...commonFilterProps, smartfitKey: smartfitKey })),
        isAccessoryOrApparel && (React.createElement(SelectAccessorySize, { ...commonFilterProps, isApparel: isApparel, fasletModel: fasletModel, sizeGuideAssetPath: sizeGuideAssetPath })),
        lowStockAlert && (React.createElement(FlexWrapper, { isFullWidth: true, mb: "32px", mt: "24px" }, lowStockAlert))));
}
ProductFilter.displayName = 'ProductFilter_Component';
